/**
 * Signature utilities for converting signature placeholders to signable areas.
 */

/**
 * Convert signature placeholders to signable areas in HTML content.
 * 
 * @param {string} html - HTML content containing signature placeholders
 * @param {Object} clientDetails - Client details for variable replacement
 * @param {Object} adviserDetails - Adviser details for variable replacement
 * @returns {string} HTML content with signature placeholders converted to signable areas
 */
export function convertSignaturePlaceholdersToSignableAreas(html, clientDetails = null, adviserDetails = null) {
  if (!html) return html;

  // Find and replace signature placeholders
  const signaturePlaceholders = [
    { placeholder: '{{ client_signature }}', label: 'Client Signature', isClient: true, isAdviser: false },
    { placeholder: '{{ adviser_signature }}', label: 'Adviser Signature', isClient: false, isAdviser: true }
  ];

  let modifiedHtml = html;

  signaturePlaceholders.forEach(({ placeholder, label, isClient, isAdviser }) => {
    if (modifiedHtml.includes(placeholder)) {
      const signatureArea = createSignatureAreaHtml(label, isClient, isAdviser, clientDetails, adviserDetails);
      modifiedHtml = modifiedHtml.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), signatureArea);
    }
  });

  return modifiedHtml;
}

/**
 * Convert signature placeholders to a signature section with both signatures side by side.
 * 
 * @param {string} html - HTML content containing signature placeholders
 * @param {Object} clientDetails - Client details for variable replacement
 * @param {Object} adviserDetails - Adviser details for variable replacement
 * @returns {string} HTML content with signature placeholders converted to a signature section
 */
export function convertToSignatureSection(html, clientDetails = null, adviserDetails = null) {
  if (!html) return html;

  // Check if both signature placeholders exist
  const hasClientSignature = html.includes('{{ client_signature }}');
  const hasAdviserSignature = html.includes('{{ adviser_signature }}');

  if (!hasClientSignature && !hasAdviserSignature) {
    return html; // No signature placeholders found
  }

  // Create signature section HTML
  const signatureSection = createSignatureSectionHtml(hasClientSignature, hasAdviserSignature, clientDetails, adviserDetails);

  // Replace the first occurrence of either signature placeholder with the signature section
  let modifiedHtml = html;
  
  if (hasClientSignature) {
    modifiedHtml = modifiedHtml.replace('{{ client_signature }}', signatureSection);
    // Remove any remaining adviser signature placeholder
    modifiedHtml = modifiedHtml.replace('{{ adviser_signature }}', '');
  } else if (hasAdviserSignature) {
    modifiedHtml = modifiedHtml.replace('{{ adviser_signature }}', signatureSection);
  }

  return modifiedHtml;
}

/**
 * Create a signature section with both signatures side by side.
 * 
 * @param {boolean} hasClientSignature - Whether to include client signature
 * @param {boolean} hasAdviserSignature - Whether to include adviser signature
 * @param {Object} clientDetails - Client details for variable replacement
 * @param {Object} adviserDetails - Adviser details for variable replacement
 * @returns {string} HTML string for the signature section
 */
function createSignatureSectionHtml(hasClientSignature, hasAdviserSignature, clientDetails = null, adviserDetails = null) {
  const signatureAreas = [];
  
  if (hasClientSignature) {
    signatureAreas.push(createSignatureAreaHtml('Client Signature', true, false, clientDetails, adviserDetails));
  }
  
  if (hasAdviserSignature) {
    signatureAreas.push(createSignatureAreaHtml('Adviser Signature', false, true, clientDetails, adviserDetails));
  }
  
  const signatureAreasHtml = signatureAreas.join('\n        ');
  
  return `
    <div class="pdf-signatures" style="display: flex; justify-content: space-between; margin: 60px 0 40px 0; padding: 0 40px; page-break-inside: avoid;">
        ${signatureAreasHtml}
    </div>
  `;
}

/**
 * Create HTML string for a signable signature area.
 * 
 * @param {string} label - Label for the signature area
 * @param {boolean} isClient - Whether this is a client signature (to include client name)
 * @param {boolean} isAdviser - Whether this is an adviser signature (to include adviser name)
 * @param {Object} clientDetails - Client details for variable replacement
 * @param {Object} adviserDetails - Adviser details for variable replacement
 * @returns {string} HTML string for the signature area
 */
function createSignatureAreaHtml(label, isClient = false, isAdviser = false, clientDetails = null, adviserDetails = null) {
  if (isClient) {
    const clientName = clientDetails?.full_name || '{{ client_name }}';
    return `
    <div class="signature-block" style="display: flex; flex-direction: column; align-items: center; width: 45%;">
        <div class="client-name" style="font-size: 1rem; color: #1a365d; font-weight: 500; margin-bottom: 12px; font-family: 'Times New Roman', Times, serif;">Client name: ${clientName}</div>
        <div class="signature-line" style="width: 100%; border-bottom: 2px solid #000; height: 40px; margin-bottom: 12px;"></div>
        <div class="signature-label" style="font-size: 1rem; color: #1a365d; font-weight: 500; margin-top: 2px; font-family: 'Times New Roman', Times, serif;">${label}</div>
    </div>
  `;
  } else if (isAdviser) {
    const adviserName = adviserDetails?.full_name || '{{ adviser_name }}';
    return `
    <div class="signature-block" style="display: flex; flex-direction: column; align-items: center; width: 45%;">
        <div class="adviser-name" style="font-size: 1rem; color: #1a365d; font-weight: 500; margin-bottom: 12px; font-family: 'Times New Roman', Times, serif;">Adviser name: ${adviserName}</div>
        <div class="signature-line" style="width: 100%; border-bottom: 2px solid #000; height: 40px; margin-bottom: 12px;"></div>
        <div class="signature-label" style="font-size: 1rem; color: #1a365d; font-weight: 500; margin-top: 2px; font-family: 'Times New Roman', Times, serif;">${label}</div>
    </div>
  `;
  }
}

/**
 * Process HTML content and convert signature placeholders to signable areas.
 * This function handles both individual placeholders and creates a signature section.
 * 
 * @param {string} html - HTML content to process
 * @param {Object} clientDetails - Client details for variable replacement
 * @param {Object} adviserDetails - Adviser details for variable replacement
 * @returns {string} Processed HTML content with signable areas
 */
export function processSignaturePlaceholders(html, clientDetails = null, adviserDetails = null) {
  if (!html) return html;

  // First, check if both signature placeholders exist in close proximity
  const hasClientSignature = html.includes('{{ client_signature }}');
  const hasAdviserSignature = html.includes('{{ adviser_signature }}');
  const hasBothSignatures = hasClientSignature && hasAdviserSignature;
  
  if (hasBothSignatures) {
    // If both exist, create a signature section
    return convertToSignatureSection(html, clientDetails, adviserDetails);
  } else if (hasClientSignature || hasAdviserSignature) {
    // If only one exists, convert individual placeholders
    return convertSignaturePlaceholdersToSignableAreas(html, clientDetails, adviserDetails);
  } else {
    return html;
  }
}
